<template>
  <teleport to="body">
    <transition name="fade">
      <div v-if="visible" class="confirm-overlay" @click.self="handleCancel">
        <div class="confirm-dialog">
          <div class="confirm-header">
            <div class="confirm-title">
              <span class="title-icon">⚠️</span>
              <span class="title-text">{{ title }}</span>
            </div>
          </div>
          
          <div class="confirm-content">
            <div class="content-text">{{ message }}</div>
          </div>
          
          <div class="confirm-footer">
            <button class="btn btn-cancel" @click="handleCancel">
              {{ cancelText }}
            </button>
            <button class="btn btn-confirm" @click="handleConfirm">
              {{ confirmText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'

const visible = ref(false)
const title = ref('提示')
const message = ref('')
const confirmText = ref('确定')
const cancelText = ref('取消')

let currentResolve = null
let currentReject = null

// 显示确认对话框
const show = (msg, options = {}, resolve, reject) => {
  message.value = msg
  title.value = options.title || '提示'
  confirmText.value = options.confirmText || '确定'
  cancelText.value = options.cancelText || '取消'
  currentResolve = resolve
  currentReject = reject
  visible.value = true
}

// 处理确认
function handleConfirm() {
  visible.value = false
  if (currentResolve) {
    currentResolve()
    currentResolve = null
    currentReject = null
  }
}

// 处理取消
function handleCancel() {
  visible.value = false
  if (currentReject) {
    currentReject()
    currentResolve = null
    currentReject = null
  }
}

// 监听 ESC 键
function handleKeydown(event) {
  if (event.key === 'Escape' && visible.value) {
    handleCancel()
  }
}

// 暴露方法供外部调用
defineExpose({
  show
})

// 全局事件监听 - 注册到 window 供 useConfirm 使用
onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  window.__confirm = {
    show
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  if (window.__confirm) {
    delete window.__confirm
  }
})
</script>

<style scoped>
.confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  backdrop-filter: blur(4px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.confirm-dialog {
  background: linear-gradient(135deg, #2a3a4f 0%, #1a2535 100%);
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.confirm-header {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
}

.confirm-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-icon {
  font-size: 24px;
}

.title-text {
  color: #fff;
  font-size: 18px;
  font-weight: 600;
}

.confirm-content {
  padding: 24px 20px;
  flex: 1;
  color: #fff;
}

.content-text {
  font-size: 15px;
  line-height: 1.6;
  text-align: center;
  word-wrap: break-word;
  color: rgba(255, 255, 255, 0.9);
}

.confirm-footer {
  padding: 16px 20px;
  display: flex;
  justify-content: center;
  gap: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
}

.btn {
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 100px;
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-1px);
}

.btn-cancel:active {
  transform: translateY(0);
}

.btn-confirm {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #000;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
}

.btn-confirm:hover {
  background: linear-gradient(135deg, #ffed4e 0%, #ffd700 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
}

.btn-confirm:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(255, 215, 0, 0.3);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .confirm-dialog {
    width: 85%;
    max-width: 350px;
  }

  .confirm-header {
    padding: 16px;
  }

  .confirm-content {
    padding: 20px 16px;
  }

  .confirm-footer {
    padding: 12px 16px;
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }

  .title-text {
    font-size: 16px;
  }

  .content-text {
    font-size: 14px;
  }
}
</style>

